/**
 * Public Routes (Login, Logout)
 *
 * These routes are outside the auth layout - accessible without authentication.
 */

import { getAuthStateSync } from '@bb/session-manager';
import { createRoute, redirect } from '@tanstack/react-router';
import React from 'react';
import { rootRoute } from './rootRoute';

// Lazy load for code splitting
const Login = React.lazy(() => import('../pages/Login/Login'));
const Logout = React.lazy(() => import('../pages/Logout/Logout'));

/**
 * Root redirect - / → /bbweb/home or /bbweb/login
 */
export const rootIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // Root always redirects into the app shell; the auth layout handles the
    // authenticated/unauthenticated split from there.
    throw redirect({ to: '/bbweb/home' });
  },
});

/**
 * Login route - public, but redirects authenticated users to home
 */
export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bbweb/login',
  beforeLoad: ({ search }: { search?: { redirect?: string } }) => {
    const authState = getAuthStateSync();
    if (authState?.isAuthenticated) {
      const target = search?.redirect;
      // Honor the redirect param if it's a valid relative path
      if (target?.startsWith('/')) {
        // Split path from query params — TanStack Router's redirect({ to })
        // treats '?' as part of the path segment, breaking route matching.
        const qIndex = target.indexOf('?');
        if (qIndex >= 0) {
          const pathname = target.substring(0, qIndex);
          const queryStr = target.substring(qIndex + 1);
          const querySearch = Object.fromEntries(new URLSearchParams(queryStr));
          throw redirect({ to: pathname, search: querySearch });
        }
        throw redirect({ to: target });
      }
      throw redirect({ to: '/bbweb/home' });
    }
  },
  component: Login,
});

/**
 * Logout route - accessible to clear session
 */
export const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bbweb/logout',
  component: Logout,
});

export const publicRoutes = [rootIndexRoute, loginRoute, logoutRoute];
